import { filter, tap } from 'rxjs/operators';
// import { PamMemberService } from '../module/pam-member/pam-member.service';
import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { BehaviorSubject, combineLatest, merge, Observable, Subject } from 'rxjs';
import { MEMBER_LOGIN_WAY } from '../entity/member-login-way';
import { GoogleService } from '../module/google-member/google.service';
import { FacebookService } from '../module/facebook-member/facebook.service';
import { LoginResultCode } from '../declaration/LoginResultCode.enum';
import { HttpErrorResponse } from '@angular/common/http';
import { OAuthError } from 'src/app/share/entity/OauthError';
import { CultureCenterService } from '../module/culture-center-member/cultureCenter.service';

/** 會員登入服務代裡
 * 只處理登入登出行為
 */
@Injectable()
export class MemberProxyService {
  isBrowser: boolean;
  /** 有任一種可用的登入方式 */
  private hasLoginSystem$ = new BehaviorSubject<boolean>(false);
  /** 有任一種可用的登入方式 */
  getHasLoginSystem$() { return this.hasLoginSystem$.asObservable(); }
  /** 有任一種可用的登入方式 */
  get hasLoginSystem() { return this.hasLoginSystem$.value; }
  /** 可使用 文化部會員中心 會員登入服務 */
  hasCultureCenterLogin: boolean;
  /** 可使用 GOOGLE 會員登入服務 */
  hasGoogleLogin: boolean;
  /** 可使用 FB 會員登入服務 */
  hasFacebookLogin: boolean;
  /** 已登入的會員資料 */
  private memberInfo$ = new BehaviorSubject<any>(null);
  /** 已登入的會員資料 */
  getMemberInfo$() { return this.memberInfo$.asObservable(); }
  /** 已登入的會員資料 */
  get memberInfo() { return this.memberInfo$.value; }
  /** 會員(手動)登出後拋出 */
  protected logout$ = new Subject<void>();
  /** 會員(手動)登出後拋出 */
  getLogout$() { return this.logout$.asObservable(); }
  /** 目前會員登入的方式 */
  loginWay: MEMBER_LOGIN_WAY;
  private apiErrorHandler$ = new BehaviorSubject<(error: any, observable) => Observable<any>>(null);
  getApiErrorHandler() { return this.apiErrorHandler$.getValue(); }
  private refreshMemberToken$ = new BehaviorSubject<(refreshToken: any) => Observable<object>>(null);
  getRefreshMemberToken() { return this.refreshMemberToken$.getValue(); }
  /** 登入開始事件，觀察者必須鎖住其他登入模式 */
  protected onLoginStart$ = new Subject<void>();
  /** 登入開始事件，觀察者必須鎖住其他登入模式 */
  getOnLoginStart$() { return this.onLoginStart$.asObservable(); }
  /** 登入結束事件，觀察者必須解鎖登入狀態 */
  protected onLoginEnd$ = new Subject<LoginResultCode>();
  /** 登入結束事件，觀察者必須解鎖登入狀態 */
  getOnLoginEnd$() { return this.onLoginEnd$.asObservable(); }

  constructor(
    private googleService: GoogleService,
    private facebookService: FacebookService,
    private cultureCenterService: CultureCenterService,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  init() {
    // 各登入服務初始化事件
    merge(
      this.cultureCenterService.getInitFinish$().pipe(
        filter(isInit => isInit && !0),
        tap(b => { this.hasCultureCenterLogin = true; })
      ),
      this.googleService.getClientId$().pipe(
        filter(id => id ? true : false),
        tap(b => { this.hasGoogleLogin = true; })
      ),
      this.facebookService.getClientId$().pipe(
        filter(id => id ? true : false),
        tap(b => { this.hasFacebookLogin = true; })
      ),
    ).subscribe(id => {
      this.hasLoginSystem$.next(true);
    });

    if (this.isBrowser) {
      // 各登入服務登入開始事件
      merge(
        this.cultureCenterService.getOnLoginStart$(),
        this.googleService.getOnLoginStart$(),
        this.facebookService.getOnLoginStart$(),
      ).subscribe(this.onLoginStart$);

      // 各登入服務登入結束事件
      merge(
        this.cultureCenterService.getOnLoginEnd$(),
        this.googleService.getOnLoginEnd$(),
        this.facebookService.getOnLoginEnd$(),
      ).subscribe(this.onLoginEnd$);

      // 各登入服務(手動)登出事件
      merge(
        this.cultureCenterService.getLogout$(),
        this.googleService.getLogout$(),
        this.facebookService.getLogout$(),
      ).subscribe(this.logout$);

      // 藝文中心 登入服務登入後取得會員資料推播
      combineLatest(
        this.cultureCenterService.getInitFinish$().pipe(filter(isInit => isInit ? true : false)),
        this.cultureCenterService.getMemberInfo$().pipe(filter(info => info ? true : false)),
      ).subscribe(([id, info]) => {
        this.loginWay = MEMBER_LOGIN_WAY.CULTURE_CENTER;
        this.memberInfo$.next(info);
        this.apiErrorHandler$.next(this.cultureCenterService.errorHandler);
        this.refreshMemberToken$.next(this.cultureCenterService.refreshMemberToken)
      });

      // GOOGLE 登入服務登入後取得會員資料推播
      combineLatest(
        this.googleService.getInitFinish$().pipe(filter(id => id ? true : false)),
        this.googleService.getMemberInfo$().pipe(filter(info => info ? true : false)),
      ).subscribe(([id, info]) => {
        this.loginWay = MEMBER_LOGIN_WAY.GOOGLE;
        this.memberInfo$.next(info);
        this.apiErrorHandler$.next(this.googleService.errorHandler);
        this.refreshMemberToken$.next(this.googleService.refreshMemberToken)
      });

      // FB 登入服務登入後取得會員資料推播
      combineLatest(
        this.facebookService.getInitFinish$().pipe(filter(id => id ? true : false)),
        this.facebookService.getMemberInfo$().pipe(filter(info => info ? true : false)),
      ).subscribe(([id, info]) => {
        this.loginWay = MEMBER_LOGIN_WAY.FACEBOOK;
        this.memberInfo$.next(info);
        this.apiErrorHandler$.next(this.facebookService.errorHandler);
        this.refreshMemberToken$.next(this.facebookService.refreshMemberToken)
      });
    }
  }

  /**
   * 登入流程
   * @param loginWay 登入方式
   * @param payload 對應方式登入服務要帶入的資料
   * @returns
   */
  login(loginWay: MEMBER_LOGIN_WAY, payload?: any): Observable<any> {
    if (loginWay) {
      switch(loginWay) {
        case MEMBER_LOGIN_WAY.CULTURE_CENTER:
          return this.cultureCenterService.login();
        case MEMBER_LOGIN_WAY.GOOGLE:
          return this.googleService.login();
        case MEMBER_LOGIN_WAY.FACEBOOK:
          return this.facebookService.login();
        default:
          throw new Error('login with non exist loginWay');
      }
    } else {
      throw new Error('login without loginWay');
    }
  }

  /** 登出流程
   * @param fromUser 是否為使用者親自(手動)點擊登出按鈕導致的登出後清資料
   */
  logout(fromUser = true, cb?: () => void): Observable<any> {
    let retObs$: Observable<any>;
    switch (this.loginWay) {
      case MEMBER_LOGIN_WAY.CULTURE_CENTER:
        retObs$ = this.cultureCenterService.logout(fromUser);
        break;
      case MEMBER_LOGIN_WAY.GOOGLE:
        retObs$ = this.googleService.logout();
        break;
      case MEMBER_LOGIN_WAY.FACEBOOK:
        retObs$ = this.facebookService.logout();
        break;
      default:
        throw new Error('logout with non exist loginWay');
    }
    if (fromUser) {
      // 應該由訂閱的地方自行決定要跳甚麼訊息，所以這裡就註解掉
      // this.globalComponentService.openInfoAlert('登出成功');
    }
    return retObs$.pipe(
      tap(() => {
        this.memberInfo$.next(null);
        this.apiErrorHandler$.next(null);
      })
    );
  }
}

/** 登入後操作 api 若失敗的訊息處理方式 */
export const printErrorHandler = (error: any, observable, globalComponentService, config, router, translateService) => {
  if (error instanceof HttpErrorResponse) {
    let oauthError: OAuthError = $.extend(new OAuthError(translateService), error.error);

    switch (error.status) {
      case 404:
        globalComponentService.openErrorAlert('提示', '找不到位址');
        break;
      case 401:
        globalComponentService.openErrorAlert('提示', '未授權的操作');
        break;
      case 403:
        globalComponentService.openErrorAlert('提示', oauthError.getMessage());
        break;
      case 612:
        const urlFragments = router.url.split('/');
        if (config.mode === 'domain') {
          // domain 模式
          const lang = urlFragments[1];
          globalComponentService.openErrorAlert('提示', oauthError.getMessage(), () => router.navigateByUrl(`/${lang}`));
        } else {
          // path 模式
          const path = urlFragments[1];
          const lang = urlFragments[2];
          globalComponentService.openErrorAlert('提示', oauthError.getMessage(), () => router.navigateByUrl(`/${path ? path : ''}/${lang}`));
        }
        break;
      default:
        globalComponentService.openErrorAlert('提示', oauthError.getMessage());
        break;
    }
  }
  return Observable.throw(error);
}
